import React, { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';

import { makeGetExternalAccountByProvider } from 'selectors/external-accounts';

import { getAccountToken } from 'api/external-accounts-api';
import { externalLoad } from 'api/files-api';

import { createAlert } from 'services/alert-service';

import config from 'config/index';

import Tooltip from 'components/tooltip';

const REDIRECT_URL = `https://${config.APP_DOMAIN}/external-redirect/microsoft`;

const OneDrivePicker = ({ projectId, taskId, docId, folderId, folderStack, disableAccountTooltip, account, multiSelect, onChange, children }) => {
    const { t } = useTranslation(['file', 'apps']);
    const history = useHistory();
    const location = useLocation();

    const handleFilesChoose = useCallback(({ value: files }) => {
        if (onChange) {
            onChange(files.map(file => ({
                ...file,
                serviceType: 'microsoft',
                serviceProps: {
                    type: 'microsoft',
                    id: file.id
                }
            })));
            return;
        }

        const alert = createAlert('success', <><i className="icon material-icons icon-animate-rotate">autorenew</i> { t('importingFile', { count: files.length, name: files[0].name } )}</>, {
            duration: 0
        });

        externalLoad({
            project: projectId,
            task: taskId,
            doc: docId,
            folder_id: folderId,
            folder_stack: folderStack,
            files: files.map(({ id }) => ({
                id,
                type: 'microsoft'
            }))
        }).then(response => {
            alert.setContent(
                <React.Fragment>
                    <i className="icon material-icons icon-animate-rotate">autorenew</i> { t('importedFile', { count: files.length }) }
                </React.Fragment>
            );
            alert.hide(5000);
        });
    }, [projectId, taskId, docId, folderId, folderStack]);

    const handleClick = useCallback(async () => {
        if (!account) {
            history.push({
                pathname: `/settings/apps`,
                state: {
                    closeUrl: location.pathname
                }
            });
            return;
        }

        const { token } = await getAccountToken('microsoft', true);

        window.OneDrive.open({
            clientId: config.MICROSOFT_CONFIG.CLIENT_ID,
            action: 'query',
            multiSelect,
            openInNewWindow: true,
            accountSwitchEnabled: false,
            advanced: {
                endpointHint: 'api.onedrive.com',
                accessToken: token,
                // redirectUri: REDIRECT_URL
            },
            success: handleFilesChoose,
            cancel: () => {},
            error: error => {
                createAlert('error', error);
            }
        });
    }, [handleFilesChoose, account, history]);

    return (
        <Tooltip disabled={disableAccountTooltip || Boolean(account)} tooltip={ t('apps:accounts.microsoft.link') }>
            <div onClick={handleClick}>
                { children }
            </div>
        </Tooltip>
    )
}

const makeMapStateToProps = () => {
    const getExternalAccountTeamByProvider = makeGetExternalAccountByProvider();
    const getExternalAccountUserByProvider = makeGetExternalAccountByProvider();

    return ({ externalAccounts }, ownProps) => {
        const teamAccount = getExternalAccountTeamByProvider(externalAccounts.items, 'microsoft', true);
        const userAccount = getExternalAccountUserByProvider(externalAccounts.items, 'microsoft', false);

        return {
            account: teamAccount || userAccount
        }
    };
}

export default connect(makeMapStateToProps)(OneDrivePicker);
